import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { COMMUNICATION_CATEGORIES } from './categories';
import { FloatingMenu } from '../../../FloatingMenu';

interface CategoryAddDropdownProps {
    triggerRef: RefObject<HTMLElement>;
    selected: string[];
    onToggle: (categoryId: string) => void;
    onClose: () => void;
}

// Searchable, multi-select checkbox menu - stays open across picks so
// building a set doesn't mean reopening it each time.
export function CategoryAddDropdown({
    triggerRef,
    selected,
    onToggle,
    onClose
}: CategoryAddDropdownProps) {
    const [filter, setFilter] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // `{ preventScroll: true }` avoids autoFocus scrolling the page (see AssigneeSelector).
    useEffect(() => {
        inputRef.current?.focus({ preventScroll: true });
    }, []);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return COMMUNICATION_CATEGORIES;
        return COMMUNICATION_CATEGORIES.filter((c) => c.id.toLowerCase().includes(q));
    }, [filter]);

    return (
        <FloatingMenu open triggerRef={triggerRef} onClose={onClose} estimatedHeight={300}>
            <div
                className="w-[248px] bg-white border border-gray-200 rounded-xl shadow-[0_12px_28px_rgba(0,0,0,0.14)] overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="h-9 px-3 border-b border-gray-100 flex items-center gap-2">
                    <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter"
                        className="flex-1 min-w-0 text-[13px] text-gray-700 placeholder-gray-400 outline-none bg-transparent"
                    />
                </div>
                <div className="py-1 max-h-[240px] overflow-y-auto">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-2 text-[12px] text-gray-400">No matches.</div>
                    ) : (
                        filtered.map((category) => {
                            const isSelected = selected.includes(category.id);
                            return (
                                <button
                                    key={category.id}
                                    type="button"
                                    onClick={() => onToggle(category.id)}
                                    className="w-full h-9 px-3 flex items-center gap-2.5 hover:bg-gray-50"
                                >
                                    <span
                                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                            isSelected
                                                ? 'bg-gray-900 border-gray-900 text-white'
                                                : 'border-gray-300'
                                        }`}
                                    >
                                        {isSelected && (
                                            <CheckIcon className="h-2.5 w-2.5" strokeWidth={3.5} />
                                        )}
                                    </span>
                                    <span
                                        className="w-2 h-2 rounded-full shrink-0"
                                        style={{ background: category.tone }}
                                    />
                                    <span className="text-[13px] font-medium text-gray-700 truncate">
                                        {category.id}
                                    </span>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </FloatingMenu>
    );
}
